import React from 'react';

function LegalPage({ type, onClose, colors, fonts }) {
  const content = type === "terms" ? {
    title: "Terms & Conditions",
    sections: [
      {
        heading: "Acceptance of Terms",
        text: "By accessing and using The Single Loop, you accept and agree to be bound by the terms and provision of this agreement."
      },
      {
        heading: "Pattern Copyrights",
        text: "All patterns linked on The Single Loop are the intellectual property of their respective creators. The Single Loop acts as a curator and aggregator, directing users to the original pattern sources. Users must respect the copyright and terms set by individual pattern creators."
      },
      {
        heading: "User Content",
        text: "By creating an account and saving patterns, you grant The Single Loop the right to store and display your saved patterns and project information for the purpose of providing the service."
      },
      {
        heading: "Affiliate Links",
        text: "Some links on The Single Loop may be affiliate links. This means that if you click a link and make a purchase, The Single Loop may earn a small commission at no additional cost to you. We only recommend products and yarns we genuinely believe in. All affiliate relationships are disclosed at the point of the link."
      },
      {
        heading: "Disclaimer",
        text: "The materials on The Single Loop are provided on an 'as is' basis. The Single Loop makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights."
      },
      {
        heading: "Limitations",
        text: "In no event shall The Single Loop or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on The Single Loop."
      },
      {
        heading: "Modifications",
        text: "The Single Loop may revise these terms of service at any time without notice. By using this website you are agreeing to be bound by the then current version of these terms of service."
      }
    ]
  } : {
    title: "Privacy Policy",
    sections: [
      {
        heading: "Information We Collect",
        text: "We collect information you provide directly to us when you create an account, including your email address and a username you choose. We authenticate users via a magic link sent to your email — no password is stored. We also collect information about the patterns you save and projects you track within the app."
      },
      {
        heading: "How We Use Your Information",
        text: "We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to personalize your experience on The Single Loop. We do not use your information for advertising or sell it to third parties."
      },
      {
        heading: "Information Sharing",
        text: "We do not sell, trade, or rent your personally identifiable information to outside parties. We use Supabase (supabase.com) for database hosting and authentication services. We use Google Fonts to display typography on the site, which may log your IP address when fonts are loaded. These third parties have their own privacy policies governing their use of your data."
      },
      {
        heading: "Affiliate Links",
        text: "Some links on The Single Loop — particularly yarn recommendations — may be affiliate links. If you click an affiliate link and make a purchase, we may earn a small commission at no extra cost to you. Affiliate links are disclosed at the point of use with an 'Affiliate Disclosure' notice. We do not allow affiliate relationships to influence which patterns or products we recommend."
      },
      {
        heading: "Cookies and Tracking",
        text: "The Single Loop uses cookies strictly necessary to keep you signed in (session cookies managed by Supabase). We do not use advertising cookies or third-party tracking cookies. Google Fonts is loaded from Google's servers, which may set cookies or log your IP address per Google's privacy policy. You can instruct your browser to refuse cookies, though this will prevent you from staying signed in."
      },
      {
        heading: "Data Security",
        text: "We implement appropriate technical and organizational measures to protect the security of your personal information. Your data is stored securely with Supabase. However, no method of transmission over the Internet or electronic storage is 100% secure."
      },
      {
        heading: "Your Rights",
        text: "You have the right to access, update, or request deletion of your personal information at any time. To exercise these rights, please contact us directly at andrea@thesingleloop.com and we will respond within 30 days. California residents have additional rights under the CCPA, including the right to know what personal information we collect and the right to non-discrimination for exercising your privacy rights. We do not sell personal information."
      },
      {
        heading: "Third-Party Links",
        text: "The Single Loop contains links to external pattern sources and, where noted, affiliate partner sites. We are not responsible for the privacy practices or content of these external sites. We encourage you to read the privacy policies of any third-party sites you visit."
      },
      {
        heading: "Children's Privacy",
        text: "The Single Loop requires users to be at least 13 years of age to create an account. We do not knowingly collect personally identifiable information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us and we will delete it promptly."
      },
      {
        heading: "Changes to This Policy",
        text: "We may update our Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the 'Last Updated' date below."
      }
    ]
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "2rem",
        overflow: "auto"
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "20px",
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "3rem",
          fontFamily: fonts.body
        }}
      >
        <h1
          style={{
            fontSize: "2.5rem",
            fontFamily: fonts.display,
            color: colors.charcoal,
            marginBottom: "1rem"
          }}
        >
          {content.title}
        </h1>

        <p style={{ color: colors.darkGray, marginBottom: "2rem", fontSize: "0.9rem" }}>
          Last Updated: February 26, 2026
        </p>

        {content.sections.map((section, idx) => (
          <div key={idx} style={{ marginBottom: "2rem" }}>
            <h2
              style={{
                fontSize: "1.3rem",
                color: colors.charcoal,
                marginBottom: "0.75rem",
                fontWeight: "700"
              }}
            >
              {section.heading}
            </h2>
            <p
              style={{
                color: colors.darkGray,
                lineHeight: "1.7",
                fontSize: "1rem"
              }}
            >
              {section.text}
            </p>
          </div>
        ))}

        <div
          style={{
            marginTop: "3rem",
            padding: "1.5rem",
            background: colors.bgCream,
            borderRadius: "12px",
            border: `2px solid ${colors.borderGreige}`
          }}
        >
          <h3 style={{ fontSize: "1.1rem", color: colors.charcoal, marginBottom: "0.5rem" }}>
            Questions?
          </h3>
          <p style={{ color: colors.darkGray, margin: 0 }}>
            If you have any questions about {type === "terms" ? "these terms" : "this privacy policy"}, please contact us at{" "}
            <a
              href="mailto:andrea@thesingleloop.com"
              style={{ color: colors.sageGreen, textDecoration: "none", fontWeight: "600" }}
            >
              andrea@thesingleloop.com
            </a>
          </p>
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: "2rem",
            width: "100%",
            padding: "1rem",
            background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
            color: "#fff",
            border: "none",
            borderRadius: "50px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          Close
        </button>

        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            background: "transparent",
            border: "none",
            fontSize: "1.5rem",
            color: colors.brownGray,
            cursor: "pointer",
            padding: "0.5rem"
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default LegalPage;
